import { t } from "@travily/i18n";
import familyIllustration from "@ui/assets/illustrations/family-animated.webp";
import { ResponsiveText } from "@ui/components/ResponsiveText";
import { TileRadioGroup } from "@ui/components/TileRadioGroup";
import { Wizard } from "@ui/components/Wizard";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import type { ReactNode } from "react";

const COLOR = "red" as const;

export interface ExperienceStepViewProps {
  currentStage: number;
  totalStages: number;
  onBack?: () => void;
  /** Wizard top-bar tertiary actions (Questions / Save and finish later). */
  topBarActions: ReactNode;
  isPending: boolean;
  value?: string | null;
  error?: string;
  onChange: (value: string) => void;
  onContinue: () => void;
}

export function ExperienceStepView({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  isPending,
  value,
  error,
  onChange,
  onContinue,
}: ExperienceStepViewProps) {
  const isAboveMd = useBreakpoint("md");
  const isAboveLg = useBreakpoint("lg");
  const selectionGroupSize = isAboveLg ? "5xl" : isAboveMd ? "4xl" : "3xl";

  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onContinue} isPending={isPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage
        illustration={<img alt="" aria-hidden="true" src={familyIllustration} />}
        heading={t("web.founding_members.creator.steps.family.experience.show.title")}
      >
        <TileRadioGroup
          className="w-full max-w-[600px] gap-5"
          color={COLOR}
          onChange={onChange}
          size={selectionGroupSize}
          value={value ?? undefined}
        >
          <TileRadioGroup.Item value="not_started">
            <ResponsiveText
              base={t("web.founding_members.creator.steps.family.experience.show.options.not_started.base")}
              md={t("web.founding_members.creator.steps.family.experience.show.options.not_started.md")}
            />
          </TileRadioGroup.Item>
          <TileRadioGroup.Item value="new">
            <ResponsiveText
              base={t("web.founding_members.creator.steps.family.experience.show.options.new.base")}
              md={t("web.founding_members.creator.steps.family.experience.show.options.new.md")}
            />
          </TileRadioGroup.Item>
          <TileRadioGroup.Item value="experienced">
            <ResponsiveText
              base={t("web.founding_members.creator.steps.family.experience.show.options.experienced.base")}
              md={t("web.founding_members.creator.steps.family.experience.show.options.experienced.md")}
            />
          </TileRadioGroup.Item>
        </TileRadioGroup>
        {error ? <Wizard.FieldError>{error}</Wizard.FieldError> : null}
      </Wizard.Stage>
    </Wizard>
  );
}
